/**
 * Centralized Status & Meaning definitions for Productivity Sessions.
 * Single Source of Truth for tooltips, badges, and the on-screen Legend modal.
 */

export const SESSION_STATUS_GLOSSARY = {
  LIVE: {
    key: 'LIVE',
    label: 'LIVE',
    category: 'Session Status',
    badgeClass: 'text-primary',
    dotClass: 'bg-primary',
    meaning: 'User session is currently active and has not yet ended.',
    detail: 'Ongoing user activity and telemetry heartbeats are actively being tracked in real time.',
    icon: 'radio_button_checked',
  },
  VALIDATED: {
    key: 'VALIDATED',
    label: 'VALIDATED',
    category: 'Session Status',
    badgeClass: 'text-emerald-700',
    dotClass: 'bg-emerald-500',
    meaning: 'Session has ended normally and the system has successfully validated the session data.',
    detail: 'Login/logout boundaries, idle durations, and productive intervals have been calculated and verified.',
    icon: 'verified',
  },
  RECOVERED: {
    key: 'RECOVERED',
    label: 'RECOVERED',
    category: 'Session Status',
    badgeClass: 'text-amber-600',
    dotClass: 'bg-amber-500',
    meaning: 'Session was interrupted/abnormally closed but was subsequently recovered/reconstructed by the system.',
    detail: 'The system automatically reconciled missing gap boundaries or reconstructed the session timeline after an unexpected interruption.',
    icon: 'healing',
  },
  MISSING: {
    key: 'MISSING',
    label: 'MISSING',
    category: 'Timeline / Logout',
    badgeClass: 'text-error',
    dotClass: 'bg-error',
    meaning: 'Login/Logout pair is incomplete; expected logout/session-end data has not yet been received or recorded.',
    detail: 'The session closed without a clean logout packet from the monitoring agent. May require administrative review or reconciliation.',
    icon: 'help',
  },
}

export const LOGOUT_LOGIC_GLOSSARY = {
  ACTIVE_SESSION: {
    key: 'ACTIVE_SESSION',
    label: 'Active Session',
    badgeClass: 'bg-emerald-100 text-emerald-800',
    meaning: 'User session is currently active and has not yet ended.',
    detail: 'Logout reason will be determined and recorded once the employee completes or exits their session.',
    icon: 'bolt',
  },
  USER_INACTIVE: {
    key: 'USER_INACTIVE',
    label: 'User Inactive',
    badgeClass: 'bg-rose-100 text-rose-800',
    meaning: 'Session was closed/logged out because the configured inactivity threshold was reached.',
    detail: 'No keyboard or mouse activity was detected within the configured idle timeout period, triggering automatic logout.',
    icon: 'timer_off',
  },
  END_OF_SHIFT: {
    key: 'END_OF_SHIFT',
    label: 'End Of Shift',
    badgeClass: 'bg-indigo-100 text-indigo-800',
    meaning: "Session was automatically closed based on the employee's configured shift end time.",
    detail: "The employee's work schedule in ShiftMaster concluded, and the session ended automatically.",
    icon: 'schedule',
  },
  SYSTEM_FAILURE: {
    key: 'SYSTEM_FAILURE',
    label: 'System Failure',
    badgeClass: 'bg-error-container text-error',
    meaning: 'Session was interrupted because of application, system, device, network or monitoring-service failure.',
    detail: 'Possible causes: Long heartbeat/network disconnect, agent app crashed/closed, device sleep/shutdown while active, or ungraceful browser exit.',
    icon: 'error',
  },
  BREAK: {
    key: 'BREAK',
    label: 'Break',
    badgeClass: 'bg-amber-100 text-amber-800',
    meaning: 'Session was paused or logged out by the employee for an authorized break.',
    detail: 'The employee manually initiated a short break interval.',
    icon: 'coffee',
  },
  LUNCH_BREAK: {
    key: 'LUNCH_BREAK',
    label: 'Lunch Break',
    badgeClass: 'bg-amber-100 text-amber-800',
    meaning: 'Session was paused or logged out by the employee for a scheduled lunch break.',
    detail: 'The employee manually initiated a meal break interval.',
    icon: 'restaurant',
  },
  MEETING: {
    key: 'MEETING',
    label: 'Meeting',
    badgeClass: 'bg-cyan-100 text-cyan-800',
    meaning: 'Session was paused or logged out to attend an offline or external meeting.',
    detail: 'Work was temporarily paused for scheduled meeting discussions.',
    icon: 'groups',
  },
  SESSION_REPLACED: {
    key: 'SESSION_REPLACED',
    label: 'Session Replaced',
    badgeClass: 'bg-purple-100 text-purple-800',
    meaning: 'Prior session was closed because the user started a new session on another device or tab.',
    detail: 'A concurrent login superseded this session.',
    icon: 'devices',
  },
  OTHERS: {
    key: 'OTHERS',
    label: 'Others',
    badgeClass: 'bg-surface-container-high text-on-surface-variant',
    meaning: 'Logout reason does not fall under the predefined automated categories.',
    detail: 'Session was manually signed out by the user or closed under standard conditions.',
    icon: 'logout',
  },
}

/**
 * Returns the glossary metadata for a session status.
 */
export function getStatusGlossaryItem(status) {
  const normalized = String(status || '').trim().toUpperCase()
  if (SESSION_STATUS_GLOSSARY[normalized]) {
    return SESSION_STATUS_GLOSSARY[normalized]
  }
  return {
    key: normalized,
    label: normalized || 'UNKNOWN',
    category: 'Session Status',
    badgeClass: 'text-on-surface-variant',
    dotClass: 'bg-outline-variant',
    meaning: 'Status is pending or unclassified.',
    detail: 'The session state is being monitored by the system.',
    icon: 'info',
  }
}

/**
 * Returns the glossary metadata for a logout logic reason.
 */
export function getLogoutLogicGlossaryItem(reason, isLiveSession = false) {
  if (isLiveSession) {
    return LOGOUT_LOGIC_GLOSSARY.ACTIVE_SESSION
  }

  const normalized = String(reason || '').trim().toUpperCase()
  if (LOGOUT_LOGIC_GLOSSARY[normalized]) {
    return LOGOUT_LOGIC_GLOSSARY[normalized]
  }

  // Handle aliases
  if (normalized === 'SYSTEM_KILL' || normalized.includes('FAILURE')) {
    return LOGOUT_LOGIC_GLOSSARY.SYSTEM_FAILURE
  }
  if (normalized === 'MANUAL') {
    return LOGOUT_LOGIC_GLOSSARY.OTHERS
  }

  return {
    key: normalized,
    label: normalized.replace(/_/g, ' ') || 'Others',
    badgeClass: 'bg-surface-container-high text-on-surface-variant',
    meaning: LOGOUT_LOGIC_GLOSSARY.OTHERS.meaning,
    detail: LOGOUT_LOGIC_GLOSSARY.OTHERS.detail,
    icon: 'info',
  }
}
